import { Button } from '@mantine/core'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import { useTranslation } from '@flecto/translations'
import { type CoreBundle } from '@flecto/types'
import { Drawer, useDrawer } from '@flecto/ui'

import { useItemGeneratorAI } from '../../hooks/useItemGeneratorAI'
import { FormBundle } from '../FormBundle/FormBundle'

export type DrawerBundleProps = {
  bundle?: Partial<CoreBundle>
  onClose?: () => void
  onError?: (message: string) => void
  onSuccess?: () => void
}

export function DrawerBundle({
  bundle,
  onClose,
  onError,
  onSuccess,
}: DrawerBundleProps) {
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)
  const [disableSaveButton, setDisableSaveButton] = useState(false)
  const { t: tActions } = useTranslation('shared', { keyPrefix: 'actions' })
  const { t } = useTranslation('saas', { keyPrefix: 'items' })
  const { closeDrawer } = useDrawer()

  const { isLoading: isLoadingAI, cancelGenerateAI } = useItemGeneratorAI()
  const shouldDisableButtons =
    (isLoadingAI.all || isLoadingAI.single || isLoading) ?? false

  const handleClose = () => {
    if (!shouldDisableButtons) {
      onClose?.()
      closeDrawer()
      cancelGenerateAI()
    }
  }

  const handleSuccess = () => {
    onSuccess?.()
    closeDrawer()
  }

  const handleError = (message: string) => {
    onError?.(message)
  }

  const title = bundle?.title ?? t('label_new_bundle')

  useEffect(() => {
    if (router?.events) {
      router.events.on('routeChangeStart', handleClose)
      return () => {
        router.events.off('routeChangeStart', handleClose)
      }
    }
  }, [router.events])

  return (
    <Drawer withContainer>
      <Drawer.Close onClick={handleClose} />
      <Drawer.Title data-testid='drawer-title'>{title}</Drawer.Title>
      <Drawer.Actions>
        <Button
          type='submit'
          form='form-bundle'
          disabled={shouldDisableButtons || disableSaveButton}
          loading={isLoading}
          data-testid='btn-save'
        >
          {tActions('save')}
        </Button>
      </Drawer.Actions>
      <Drawer.Content>
        <FormBundle
          id='form-bundle'
          data-testid='form-bundle'
          bundle={bundle}
          onChangeLoading={setIsLoading}
          disableSaveButton={setDisableSaveButton}
          onError={handleError}
          onSuccess={handleSuccess}
        />
      </Drawer.Content>
    </Drawer>
  )
}
